/**
 * Shared icon utilities for library resources
 * Centralized icon rendering to avoid duplication across components
 */

import type { LibraryResourceType, LibraryDifficulty } from "@/types/models";

/**
 * Get the icon component for a library resource type.
 *
 * @param type - The resource type. Accepts `undefined` or `null` as a fallback
 *   for anomalous data (e.g., missing type from API). This should be treated as
 *   an edge case indicating upstream data issues.
 * @param size - Icon size variant
 * @returns SVG icon element, or a default document icon for unknown/missing types
 */
export function getResourceTypeIcon(
  type: LibraryResourceType | undefined | null,
  size: "sm" | "md" | "lg" = "md",
) {
  const sizeClass = {
    sm: "w-4 h-4",
    md: "w-5 h-5",
    lg: "w-6 h-6",
  }[size];

  // Default icon for unknown/undefined types
  const defaultIcon = (
    <svg
      className={sizeClass}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  );

  if (!type) {
    return defaultIcon;
  }

  switch (type) {
    case "article":
      return (
        <svg
          className={sizeClass}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      );
    case "video":
      return (
        <svg
          className={sizeClass}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      );
    case "pdf":
      return (
        <svg
          className={sizeClass}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
          />
        </svg>
      );
    case "tool":
      return (
        <svg
          className={sizeClass}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
      );
    case "course":
      return (
        <svg
          className={sizeClass}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
          />
        </svg>
      );
    case "documentation":
      return (
        <svg
          className={sizeClass}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
          />
        </svg>
      );
    case "blog":
      return (
        <svg
          className={sizeClass}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 12h10"
          />
        </svg>
      );
    default:
      // Handle unexpected string values (future enum values or malformed data)
      return defaultIcon;
  }
}

/**
 * Get the background/text color styles for a resource type icon container.
 *
 * @param type - The resource type. Accepts `undefined` or `null` as a fallback
 *   for anomalous data (e.g., missing type from API). This should be treated as
 *   an edge case indicating upstream data issues.
 * @returns Tailwind CSS classes for background and text colors
 */
export function getResourceTypeIconStyles(
  type: LibraryResourceType | undefined | null,
): string {
  switch (type) {
    case "article":
      return "bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400";
    case "video":
      return "bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400";
    case "pdf":
      return "bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400";
    case "tool":
      return "bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400";
    case "course":
      return "bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400";
    case "documentation":
      return "bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400";
    case "blog":
      return "bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400";
    default:
      return "bg-gray-100 dark:bg-gray-900/30 text-gray-600 dark:text-gray-400";
  }
}

/**
 * Get the badge color styles for a difficulty level
 */
export function getDifficultyStyles(difficulty: LibraryDifficulty): string {
  switch (difficulty) {
    case "beginner":
      return "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400";
    case "intermediate":
      return "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400";
    case "advanced":
      return "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400";
  }
}

/**
 * Format a topic slug to a readable label
 */
export function formatTopic(topic: string): string {
  return topic
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * Format resource type to display label.
 *
 * @param type - The resource type. Accepts `undefined` or `null` as a fallback
 *   for anomalous data (e.g., missing type from API). This should be treated as
 *   an edge case indicating upstream data issues.
 * @returns Human-readable label (e.g., "Article", "Video") or "Resource" for unknown types
 */
export function formatResourceType(
  type: LibraryResourceType | undefined | null,
): string {
  if (!type) return "Resource";
  return type.charAt(0).toUpperCase() + type.slice(1);
}
